import React, { useMemo, useState } from 'react';
import { normalizeStudentNameInput } from '../../utils/studentNamePolicy';
import {
  ROSTER_MAX_STUDENTS as MAX_STUDENTS,
  parsePastedNames,
  studentsChanged,
  toEditableStudents,
} from '../../utils/classRosterManage';

const ClassRosterEditor = ({ roster, saving = false, onSave, onCancel }) => {
  const [label, setLabel] = useState(roster.label || '');
  const [semester, setSemester] = useState(roster.semester === 2 ? 2 : 1);
  const [students, setStudents] = useState(() => toEditableStudents(roster));
  const [newName, setNewName] = useState('');

  const dirty = useMemo(() => (
    normalizeStudentNameInput(label) !== (roster.label || '')
    || semester !== (roster.semester === 2 ? 2 : 1)
    || studentsChanged(roster, students)
  ), [label, roster, semester, students]);
  const filledCount = students.filter(s => normalizeStudentNameInput(s.name)).length;

  const updateAt = (index, patch) => setStudents(prev => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const removeAt = (index) => setStudents(prev => prev.filter((_s, i) => i !== index));
  const move = (index, delta) => setStudents((prev) => {
    const target = index + delta;
    if (target < 0 || target >= prev.length) return prev;
    const next = [...prev];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const removeAlias = (index, alias) => updateAt(index, { aliases: students[index].aliases.filter(a => a !== alias) });

  const addNames = (names) => {
    const clean = names.map(normalizeStudentNameInput).filter(Boolean);
    if (!clean.length) return;
    setStudents(prev => [...prev, ...clean.map(name => ({ sid: '', name, aliases: [] }))].slice(0, MAX_STUDENTS));
    setNewName('');
  };

  const handleAddPaste = (event) => {
    const text = event.clipboardData?.getData('text') || '';
    if (!/\r?\n/.test(text.trim())) return;
    event.preventDefault();
    addNames(parsePastedNames(text));
  };

  const handleSave = () => {
    if (!dirty || saving || filledCount < 1) return;
    onSave?.({ label: normalizeStudentNameInput(label), semester, students });
  };

  return (
    <div className="classes-editor" data-testid="classes-editor">
      <div className="classes-editor-head">
        <label className="classes-field">
          <span>명단 이름</span>
          <input
            className="classes-input"
            value={label}
            maxLength={40}
            onChange={e => setLabel(e.target.value)}
            aria-label="명단 이름"
          />
        </label>
        <div className="classes-field">
          <span>학기</span>
          <div className="classes-segment" role="group" aria-label="학기">
            {[1, 2].map(value => (
              <button
                key={value}
                type="button"
                className={semester === value ? 'is-active' : ''}
                aria-pressed={semester === value}
                onClick={() => setSemester(value)}
              >
                {value}학기
              </button>
            ))}
          </div>
        </div>
      </div>

      <ol className="classes-student-list">
        {students.map((student, index) => (
          <li key={student.sid || `new-${index}`} className="classes-student-row">
            <span className="classes-student-number">{index + 1}</span>
            <div className="classes-student-main">
              <input
                className="classes-input"
                value={student.name}
                maxLength={30}
                onChange={e => updateAt(index, { name: e.target.value })}
                aria-label={`${index + 1}번 학생 이름`}
              />
              {student.aliases.length > 0 && (
                <div className="classes-alias-row">
                  <span>이렇게 적혀도 이 학생이에요</span>
                  {student.aliases.map(alias => (
                    <button
                      key={alias}
                      type="button"
                      className="classes-alias-chip"
                      onClick={() => removeAlias(index, alias)}
                      aria-label={`${alias} 별칭 빼기`}
                    >
                      {alias}
                      <span aria-hidden="true">×</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="classes-student-actions">
              <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`${index + 1}번 위로`}>↑</button>
              <button type="button" onClick={() => move(index, 1)} disabled={index === students.length - 1} aria-label={`${index + 1}번 아래로`}>↓</button>
              <button type="button" className="is-danger" onClick={() => removeAt(index)} aria-label={`${index + 1}번 빼기`}>빼기</button>
            </div>
          </li>
        ))}
      </ol>

      {students.length < MAX_STUDENTS && (
        <div className="classes-add-row">
          <input
            className="classes-input"
            value={newName}
            maxLength={30}
            placeholder="학생 이름 (여러 명은 붙여넣기)"
            aria-label="추가할 학생 이름"
            onChange={e => setNewName(e.target.value)}
            onPaste={handleAddPaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                addNames([newName]);
              }
            }}
          />
          <button type="button" className="classes-btn-secondary" onClick={() => addNames([newName])} disabled={!newName.trim()}>
            학생 추가
          </button>
        </div>
      )}

      <div className="classes-editor-footer">
        <span className="classes-editor-count">{filledCount}명</span>
        <button type="button" className="classes-btn-secondary" onClick={onCancel} disabled={saving}>닫기</button>
        <button
          type="button"
          className="classes-btn-primary"
          onClick={handleSave}
          disabled={!dirty || saving || filledCount < 1}
          data-testid="classes-editor-save"
        >
          {saving ? '저장하는 중…' : '변경 저장하기'}
        </button>
      </div>
    </div>
  );
};

export default ClassRosterEditor;
